import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Rocket, Globe, Loader2, CheckCircle2, XCircle, ExternalLink } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Compact per-post deploy control shown ONLY next to posts authored in this
 * project. Red rocket = push to WP as a draft post; green globe = publish live
 * (with confirmation). Existing live posts never render this.
 */
export const PostDeployControl = ({ slug, title }) => {
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const poll = (jobId, mode) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/wp/deploy/${jobId}`);
        setJob(data);
        if (data.status === "completed" || data.status === "failed") {
          clearInterval(pollRef.current);
          if (data.status === "completed") {
            toast.success(mode === "live" ? "Post published LIVE on the blog." : "Post saved to WordPress as a draft.");
          } else toast.error("Post deployment failed \u2014 see the Deploy Engine log.");
        }
      } catch (e) {
        /* keep polling */
      }
    }, 2000);
  };

  const start = async (mode) => {
    setStarting(true);
    try {
      const { data } = await axios.post(`${API}/wp/deploy-post`, {
        slug,
        mode,
        confirm: mode === "live",
      });
      setJob({ id: data.job_id, status: "queued", steps: [], mode });
      poll(data.job_id, mode);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not start post deployment.");
    } finally {
      setStarting(false);
    }
  };

  const running = job && (job.status === "queued" || job.status === "running");

  if (running) {
    return (
      <span data-testid={`post-deploy-${slug}-progress`} className="inline-flex items-center gap-1.5 text-[11px] font-nunito font-extrabold uppercase tracking-[0.1em] text-amber-600">
        <Loader2 size={12} className="animate-spin" /> Deploying…
      </span>
    );
  }

  if (job?.status === "completed" && job.result) {
    return (
      <span data-testid={`post-deploy-${slug}-done`} className="inline-flex items-center gap-2">
        <span className="inline-flex items-center gap-1 text-[11px] font-nunito font-extrabold uppercase tracking-[0.1em] text-emerald-600">
          <CheckCircle2 size={12} /> {job.result.mode === "live" ? "Live" : "Draft saved"}
        </span>
        <a
          href={job.result.mode === "live" && job.result.live_link ? job.result.live_link : job.result.edit_link}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`post-deploy-${slug}-link`}
          className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#ee2e24] hover:underline"
        >
          <ExternalLink size={11} /> Open
        </a>
      </span>
    );
  }

  if (job?.status === "failed") {
    return (
      <button type="button" onClick={() => start(job?.mode || "draft")} data-testid={`post-deploy-${slug}-retry`} className="inline-flex items-center gap-1 text-[11px] font-nunito font-extrabold uppercase tracking-[0.1em] text-[#ee2e24] hover:underline">
        <XCircle size={12} /> Failed — retry
      </button>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5">
      <button
        type="button"
        onClick={() => start("draft")}
        disabled={starting}
        title="Push this post to WordPress as a draft"
        data-testid={`post-deploy-${slug}-draft-button`}
        className="inline-flex items-center gap-1.5 rounded-full bg-[#ee2e24] px-3 py-1.5 font-nunito text-[10px] font-extrabold uppercase tracking-[0.1em] text-white hover:bg-[#d92820] transition-colors duration-150 disabled:opacity-60"
      >
        <Rocket size={11} /> Draft
      </button>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <button
            type="button"
            disabled={starting}
            title="Publish this post live"
            data-testid={`post-deploy-${slug}-live-button`}
            className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-3 py-1.5 font-nunito text-[10px] font-extrabold uppercase tracking-[0.1em] text-white hover:bg-emerald-700 transition-colors duration-150 disabled:opacity-60"
          >
            <Globe size={11} /> Live
          </button>
        </AlertDialogTrigger>
        <AlertDialogContent data-testid={`post-deploy-${slug}-confirm`} className="rounded-[14px]">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-nunito font-extrabold text-[#383838]">
              Publish this post live?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#6e6e6e]">
              “{title}” will be published immediately on the public Slingshot blog. You can
              unpublish or edit it anytime in wp-admin.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => start("live")}
              data-testid={`post-deploy-${slug}-confirm-yes`}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-nunito font-extrabold uppercase tracking-[0.08em] text-[12px]"
            >
              Yes, Publish Live
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </span>
  );
};
